import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';
import { useStore } from 'src/contexts/StoreContext';
import {
  PickerList,
  PickerListMobile,
  STEPS,
  StepsToComplete,
  StepsToCompleteOnlyPicker,
  StepsToCompleteHappyCase,
} from './UploadSteps';
import { DiscountScale } from '@photosi/expo-photosi-sdk/build/Project';
import { BoxStyleInfo, FormatInfo, LookupChoiceForStep } from '@photosi/expo-photosi-sdk/build/PrintUtils';
import { useStepper } from 'src/editor-v3/hooks/useStepper';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { isHappyCase, isPhotoKit } from 'src/helpers/group.helper';
import { useTypedNavigation } from 'src/editor-v3/hooks/useTypedNavigation';
import { useTypedRoute } from 'src/editor-v3/hooks/useTypedRoute';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { GROUP_CODES } from 'src/constants/code.constants';
import amplitude from 'src/_shared/amplitude';
import braze from 'src/_shared/braze';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { navigationResetTo } from 'src/editor-v3/helpers/navigation.helper';
import { PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';

export type DataState = {
  cards: FormatInfo[] | BoxStyleInfo[] | LookupChoiceForStep[];
  discount: DiscountScale[];
};

export const useUploadPickerLogic = () => {
  const {
    project: {
      activeProject,
      getMinDiscountScales,
      groupCode,
      activeGroup,
      startAddingPhotosFlowV3,
      productConfigurationId,
    },
    i18n,
    data: { setUploadError, showNewImageNotification, isReplacingPhoto, setReplacingPhoto },
    toastStore,
    drawer,
  } = useStore();
  const isAnyProductPresent = (activeProject?.productsCount ?? 0) > 0;
  const navigation = useTypedNavigation<ROUTE_NAMES.Upload>();
  const route = useTypedRoute<ROUTE_NAMES.Upload>();
  const { groupCodeExternal, skipConfigurationStep, skipStepValue, replacePhoto, ...filteredParams } =
    route.params ?? {};

  const code = (groupCodeExternal ??
    activeGroup?.code ??
    groupCode ??
    filteredParams?.groupCode ??
    GROUP_CODES.PRINT_CLASSIC) as GROUP_CODES;

  const isHappyCaseGroup = isHappyCase(code);
  const isPhotokitGroup = isPhotoKit(code);
  const groupTypes = activeProject?.groupTypes ?? [];
  const isGroudpIdToSend = (isHappyCaseGroup || isPhotokitGroup) && skipConfigurationStep && code === activeGroup?.code;

  const productConfigurationIdExt =
    groupTypes?.find(item => item.code === groupCodeExternal)?.productConfigurationIdDefault ||
    activeGroup?.currectProductConfigurationId ||
    productConfigurationId;

  const { isDesktop } = useResponsiveScreen();
  const [selectedCard, setSelectedCard] = useState<string | undefined>(skipStepValue);
  const [selectedFormatId, setSelectedFormatId] = useState<string | undefined>(skipStepValue);
  const [selectedFormat, setSelectedFormat] = useState<FormatInfo | BoxStyleInfo | undefined>(undefined);
  const [selectedPaperTypeId, setSelectedPaperTypeId] = useState<string | undefined>();
  const [selectedPaperFinishId, setSelectedPaperFinishId] = useState<string | undefined>();
  const navigationRef = useRef<'forward' | 'back' | 'none'>('none');
  const [data, setData] = useState<DataState>({
    cards: [],
    discount: [],
  });
  const [isUploadingPhoto, setUploadingPhoto] = useState<boolean>(false);

  const getConfiguration = useMemo(() => {
    if (isHappyCaseGroup) {
      return {
        stepsToComplete: !skipConfigurationStep ? StepsToCompleteHappyCase : StepsToCompleteOnlyPicker,
        initialStep: !skipConfigurationStep ? STEPS.BoxStyle : STEPS.Picker,
      };
    } else if (isPhotokitGroup) {
      return {
        stepsToComplete: StepsToCompleteOnlyPicker,
        initialStep: STEPS.Picker,
      };
    }
    return {
      stepsToComplete: !skipConfigurationStep ? StepsToComplete : StepsToCompleteOnlyPicker,
      initialStep: !skipConfigurationStep ? STEPS.Format : STEPS.Picker,
    };
  }, [isHappyCaseGroup, isPhotokitGroup, skipConfigurationStep]);

  const { step, goToNextStep, goBack, reset, isLastStep, isFirstStep } = useStepper<STEPS>(getConfiguration);

  const handleBack = useCallback(() => {
    if (!isFirstStep) {
      navigationRef.current = 'back';
      setData(prev => ({ ...prev, cards: [] }));
      goBack();
    } else {
      navigation.canGoBack()
        ? navigation.goBack()
        : navigationResetTo(navigation, ROUTE_NAMES.EditorNew, {
            ...filteredParams,
          });
    }
  }, [isFirstStep]);

  useEffect(() => {
    setReplacingPhoto(replacePhoto);
  }, []);

  useEffect(() => {
    if (showNewImageNotification && selectedCard) {
      toastStore.setData(selectedCard);
    }
  }, [showNewImageNotification]);

  // Resolver: loads cards on-demand for each step, auto-skips optional steps when no data
  useEffect(() => {
    const getDefault = (cards: LookupChoiceForStep[]) =>
      cards.find(c => c.isDefault)?.productConfigurationId ?? cards[0]?.productConfigurationId;

    const resolveStep = async () => {
      const discountPromise = code
        ? getMinDiscountScales(code, selectedCard ? selectedCard : productConfigurationIdExt)
        : Promise.resolve([]);

      if (step === STEPS.Format || skipConfigurationStep === STEPS.Format) {
        const [discount, formats] = await Promise.all([discountPromise, activeProject?.getAllFormatsList(code)]);

        setData(prev => ({ ...prev, cards: formats ?? [], discount }));
        if (selectedFormatId) {
          setSelectedCard(selectedFormatId);
          const selected = formats?.find(item => item.productConfigurationId === selectedFormatId);
          setSelectedFormat(selected);
        }
        navigationRef.current = 'none';
      }

      if (step === STEPS.BoxStyle || skipConfigurationStep === STEPS.BoxStyle) {
        const [discount, boxes] = await Promise.all([
          discountPromise,
          activeProject?.getBoxStyles(productConfigurationIdExt, code),
        ]);

        setData(prev => ({ ...prev, cards: boxes || [], discount }));
        if (selectedFormatId) {
          setSelectedCard(selectedFormatId);
          const selected = boxes?.find(item => item.productConfigurationId === selectedFormatId);
          setSelectedFormat(selected);
        }
        navigationRef.current = 'none';
      }

      if (step === STEPS.PaperType) {
        try {
          const [discount, types] = await Promise.all([
            discountPromise,
            activeProject?.getPaperTypes(code, selectedFormatId!),
          ]);

          if (!types?.length) {
            if (navigationRef.current === 'back') {
              goBack();
            } else {
              goToNextStep();
            }
            return;
            drawer.closeDrawer();
          }

          const exist = types.some(item => item.productConfigurationId === selectedPaperTypeId);
          setData(prev => ({ ...prev, cards: types, discount: discount ?? [] }));
          setSelectedCard(exist ? selectedPaperTypeId : getDefault(types));
          navigationRef.current = 'none';
        } catch {
          if (navigationRef.current === 'back') {
            goBack();
          } else {
            goToNextStep();
          }
        }
      }

      if (step === STEPS.PaperFinish) {
        const currentId = selectedPaperTypeId ?? selectedFormatId;
        try {
          const [discount, finishes] = await Promise.all([
            discountPromise,
            activeProject?.getPaperFinish(code, currentId!),
          ]);

          if (!finishes?.length) {
            if (navigationRef.current === 'back') {
              goBack();
            } else {
              goToNextStep();
            }
            return;
          }
          const exist = finishes.some(item => item.productConfigurationId === selectedPaperFinishId);
          setData(prev => ({ ...prev, cards: finishes, discount: discount ?? [] }));
          setSelectedCard(exist ? selectedPaperFinishId : getDefault(finishes));
          navigationRef.current = 'none';
        } catch {
          if (navigationRef.current === 'back') {
            goBack();
          } else {
            goToNextStep();
          }
        }
      }
    };

    resolveStep();
  }, [step]);

  // Discount: updates independently when user changes selection in Format/BoxStyle step
  useEffect(() => {
    if (code && [STEPS.Format, STEPS.BoxStyle].includes(step)) {
      getMinDiscountScales(code, selectedCard ?? productConfigurationIdExt).then(discount =>
        setData(prev => ({ ...prev, discount })),
      );
    }
  }, [code, selectedCard, step]);

  useEffect(() => {
    if (step !== STEPS.Loading && step !== STEPS.Picker) {
      amplitude.trackEvent(AnalyticsEvents.lookup.view, {
        product_group: code,
        lookupcode: step,
      });
    } else if (step === STEPS.Picker) {
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.view, { type: 'automatic' });
      braze.trackEvent(AnalyticsEvents.dynamicPicker.view);
    }
  }, [step]);

  const getCards = useMemo(() => {
    switch (step) {
      case STEPS.Format:
      case STEPS.BoxStyle:
      case STEPS.PaperType:
      case STEPS.PaperFinish:
        return data?.cards;
      case STEPS.Picker:
        return isDesktop ? PickerList : PickerListMobile;
      default:
        return [];
    }
  }, [step, data?.cards, isDesktop]);

  const onConfirmStep = useCallback(
    (error?: boolean, productImages?: PickedImage[], googleAccessToken?: string) => {
      if (error) {
        setUploadError(false);
        handleBack();
        return;
      }

      const selectedCardInfo = data?.cards?.find(item => item.productConfigurationId === selectedCard);
      if (selectedCardInfo && step !== STEPS.Loading && step !== STEPS.Picker) {
        amplitude.trackEvent(AnalyticsEvents.lookup.continueClick, {
          product_group: code,
          lookupcode: step,
          lookupId: 'code' in selectedCardInfo ? selectedCardInfo.code : selectedCardInfo?.name,
        });
      }

      // Save selection for current step
      if (step === STEPS.Format) {
        setSelectedFormatId(selectedCard);
        setSelectedFormat(selectedCardInfo as FormatInfo);
      } else if (step === STEPS.BoxStyle) {
        setSelectedFormat(selectedCardInfo as BoxStyleInfo);
      } else if (step === STEPS.PaperType) {
        setSelectedPaperTypeId(selectedCard);
      } else if (step === STEPS.PaperFinish) {
        setSelectedPaperFinishId(selectedCard);
      }

      if (!isLastStep) {
        navigationRef.current = 'forward';
        setData(prev => ({ ...prev, cards: [] }));
        goToNextStep();
      } else {
        if (isReplacingPhoto) {
          setReplacingPhoto(undefined);
          activeProject?.deselectAllThumbnailImages();
          navigationResetTo(navigation, ROUTE_NAMES.EditorNew, { ...filteredParams });
          return;
        }
        startAddingPhotosFlowV3(
          {
            initialProductConfigurationId: selectedCard ? selectedCard : productConfigurationIdExt,
            groupCode: code,
            images: productImages ?? [],
            ...(isGroudpIdToSend && { groupId: activeGroup?.groupIdSDK ? activeGroup.groupIdSDK : activeGroup?.id }),
            googleAccessToken,
          },
          navigation,
          filteredParams,
          reset,
        );
      }
    },
    [
      isLastStep,
      handleBack,
      goToNextStep,
      startAddingPhotosFlowV3,
      productConfigurationIdExt,
      code,
      navigation,
      filteredParams,
      setUploadError,
      reset,
      selectedCard,
      step,
    ],
  );

  const isRightVisible = step === STEPS.Picker;
  const rightOpacitySV = useSharedValue(0);
  const leftFlexSV = useSharedValue(1);
  const rightFlexSV = useSharedValue(0);

  useEffect(() => {
    if (isRightVisible) {
      leftFlexSV.value = withSpring(3, { damping: 30, stiffness: 300 });
      rightFlexSV.value = withSpring(1, { damping: 30, stiffness: 300 });
      rightOpacitySV.value = withSpring(1, { damping: 30, stiffness: 300 });
    } else {
      leftFlexSV.value = withSpring(1, { damping: 60, stiffness: 300 });
      rightFlexSV.value = withSpring(0, { damping: 60, stiffness: 300 });
      rightOpacitySV.value = withSpring(0, { damping: 60, stiffness: 300 });
    }
  }, [isRightVisible]);

  const leftAnimatedStyle = useAnimatedStyle(() => ({
    flex: leftFlexSV.value,
  }));

  const rightAnimatedStyle = useAnimatedStyle(() => ({
    flex: rightFlexSV.value,
    opacity: rightOpacitySV.value,
    display: rightFlexSV.value > 0 ? 'flex' : 'none',
  }));

  return {
    i18n,
    discount: data?.discount,
    selectedCard,
    setSelectedCard,
    step,
    handleBack,
    onConfirmStep,
    isUploadingPhoto,
    setUploadingPhoto,
    selectedFormat,
    getCards,
    leftAnimatedStyle,
    rightAnimatedStyle,
    isRightVisible,
    isHappyCaseGroup,
    isPhotokitGroup,
    isAnyProductPresent,
    groupCode: code,
  };
};
